import { useState } from "react";
import { Pencil, Power } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ROLES, type Opportunity } from "@salesboard/contracts";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DataTable, type Column } from "@/components/ui/data-table/DataTable";
import { DetailAction, DetailFact, DetailModal } from "@/components/ui/DetailModal";
import { StatusChip } from "@/components/ui/StatusChip";
import { PaginationControl } from "@/components/ui/data-table/PaginationControl";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { useServerTable } from "@/hooks/useServerTable";
import { ApiClientError } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { useSession } from "@/features/auth/useSession";
import { useOpenDetail } from "@/app/useOpenDetail";
import { useCanonicalSlug } from "@/app/useCanonicalSlug";
import { useDocumentTitle } from "@/app/useDocumentTitle";
import { useT } from "@/i18n";
import { useOpportunities } from "@/features/opportunities/hooks";
import { ClientFormDialog } from "./ClientFormDialog";
import { useClientBySlug, useOpenOpportunityCount, useSetClientActive } from "./hooks";

/**
 * One client, with its own pipeline inside it.
 *
 * A modal carrying a table is unusual, and it is what the developer asked for: opening an
 * opportunity from here **replaces** this modal rather than stacking a second one on it, so
 * there is only ever one detail on screen. Closing that opportunity steps back to this
 * client, and closing again returns to the registry.
 *
 * The embedded list keeps its **own** URL namespace and is deliberately unaffected by the
 * pipeline's filters: this is the client's list, not a filtered view of the board.
 */
export function ClientModal(): React.JSX.Element {
  const t = useT();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const session = useSession();
  const openDetail = useOpenDetail();

  const query = useClientBySlug(slug);
  // A link taken before a rename opens the right record; this makes the address bar
  // catch up, so the stale slug is not copied onward from here.
  useCanonicalSlug(slug, query.data?.slug, "/client");
  const client = query.data;
  useDocumentTitle(client?.name);
  const openCount = useOpenOpportunityCount(client?.id);
  const setActive = useSetClientActive(client?.id ?? "");

  const [renameOpen, setRenameOpen] = useState(false);
  const [activeConfirmOpen, setActiveConfirmOpen] = useState(false);

  // Its own table id, so paging this list never disturbs the pipeline's URL state.
  const table = useServerTable(`client-opportunities`, {}, { field: "expectedCloseDate", direction: "asc" });
  const opportunities = useOpportunities(
    { ...table.queryParams, client: client?.id },
    { enabled: Boolean(client?.id) },
  );

  if (query.isPending) {
    return (
      <DetailModal title={t("client.loading")}>
        <LoadingState rows={5} label={t("client.loading")} />
      </DetailModal>
    );
  }

  if (query.error || !client) {
    return (
      <DetailModal title={t("client.notFound")}>
        <ErrorState
          message={query.error instanceof ApiClientError ? query.error.message : t("client.loadFailed")}
          requestId={query.error instanceof ApiClientError ? query.error.requestId : "unknown"}
          onRetry={() => void query.refetch()}
          action={{ label: t("client.backToClients"), onClick: () => void navigate("/clients", { replace: true }) }}
        />
      </DetailModal>
    );
  }

  const canManage = session.data?.role === ROLES.headOfSales;

  const columns: Column<Opportunity>[] = [
    { key: "name", header: t("column.opportunity"), render: (row) => <span className="font-medium">{row.name}</span> },
    { key: "stage", header: t("column.stage"), render: (row) => row.stageName },
    { key: "nip", header: t("column.expectedRevenue"), align: "right", render: (row) => formatCurrency(row.nip) },
    { key: "expectedCloseDate", header: t("column.expectedClose"), render: (row) => formatDate(row.expectedCloseDate) },
    { key: "ownerName", header: t("column.owner"), render: (row) => row.ownerName },
  ];

  return (
    <>
      <DetailModal
        title={client.name}
        wide
        /*
         * The two actions as icons in the header's corner (2026-08-26), matching the
         * opportunity detail: they were labelled buttons in a row above the client's
         * pipeline, where they read as actions on the *table* rather than on the client, and
         * they were the first thing under the identity — before the facts the reader opened
         * this for.
         */
        actions={
          canManage ? (
            <>
              <DetailAction label={t("common.rename")} onClick={() => setRenameOpen(true)}>
                <Pencil className="h-4 w-4" aria-hidden />
              </DetailAction>
              <DetailAction
                label={client.active ? t("common.deactivate") : t("common.reactivate")}
                destructive={client.active}
                onClick={() => setActiveConfirmOpen(true)}
              >
                <Power className="h-4 w-4" aria-hidden />
              </DetailAction>
            </>
          ) : null
        }
        header={
          <div className="space-y-2">
            {/* The only row level with the action icons and the close ✕, so the only one that
                has to clear them. */}
            <div className={cn("min-w-0", canManage && "pr-28")}>
              <p className="text-muted-foreground text-sm">{t("client.label")}</p>
              <h2 className="text-xl font-semibold">{client.name}</h2>
            </div>

            {/*
             * Live work first, state last (2026-08-26). A reader opens a client to ask "how
             * much is going on here"; "active" is the answer to a question they only ask when
             * something looks wrong, and leading with it put the least-consulted fact in the
             * most-read position.
             */}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
              <DetailFact label={t("column.openOpportunities")}>{client.openOpportunities}</DetailFact>
              <DetailFact label={t("column.openPipeline")}>{formatCurrency(client.openPipelineValue)}</DetailFact>
              {/* The shared chip, as in the register: a word carries the state, the colour
                  only reinforces it — an inactive client takes no new work. */}
              <DetailFact label={t("column.state")}>
                <StatusChip
                  on={client.active}
                  label={client.active ? t("clients.active") : t("clients.inactive")}
                />
              </DetailFact>
            </dl>
          </div>
        }
      >
        <div className="space-y-6">
          {/*
           * No heading: it named the only thing in this section, which the table's own column
           * headers already name (2026-08-26). The accessible name survives on the table's
           * `caption`, so nothing was lost for a screen reader.
           */}
          <section>
            {opportunities.isPending && <LoadingState rows={4} label={t("opportunities.loading")} />}

            {opportunities.data && opportunities.data.items.length === 0 && (
              <EmptyState
                title={t("client.noOpportunities")}
                description={t("client.noOpportunitiesFor", client.name)}
              />
            )}

            {opportunities.data && opportunities.data.items.length > 0 && (
              <>
                {/* Flush with the dialog: `-mx-6` cancels the modal body's own padding for the
                    table alone (2026-08-26). The inset table drew a rounded border a few
                    pixels inside the modal's, which read as a panel floating in a panel. The
                    loading, empty and pagination blocks stay inset — they are prose and
                    controls, not a full-width grid. */}
                <div className="-mx-6">
                  <DataTable
                    columns={columns}
                    rows={opportunities.data.items}
                    rowKey={(row) => row.id}
                    sort={table.sort}
                    onToggleSort={table.toggleSort}
                    // Replaces this modal: the opportunity becomes the detail on screen, and
                    // closing it steps back to this client.
                    onRowClick={(row) => openDetail(`/opportunity/${row.slug}`)}
                    caption={t("client.opportunitiesCaption", client.name)}
                    // Edge to edge: no rounding and no side borders, because the dialog's own
                    // wall is now the table's left and right edge.
                    flush
                  />
                </div>
                <PaginationControl
                  page={opportunities.data.page}
                  perPage={opportunities.data.perPage}
                  total={opportunities.data.total}
                  onPageChange={table.setPage}
                  onPerPageChange={table.setPerPage}
                />
              </>
            )}
          </section>
        </div>
      </DetailModal>

      {renameOpen && (
        <ClientFormDialog open={renameOpen} onOpenChange={setRenameOpen} mode={{ kind: "rename", id: client.id, name: client.name, version: client.version }} />
      )}

      <ConfirmDialog
        open={activeConfirmOpen}
        onOpenChange={setActiveConfirmOpen}
        title={client.active ? t("client.deactivateTitle", client.name) : t("client.reactivateTitle", client.name)}
        consequence={
          client.active
            ? t("client.deactivateConsequence", t("count.opportunities", openCount.data?.count ?? 0))
            : t("client.reactivateConsequence")
        }
        confirmLabel={client.active ? t("client.deactivateConfirm") : t("client.reactivateConfirm")}
        destructive={client.active}
        pending={setActive.isPending}
        onConfirm={async () => {
          await setActive.mutateAsync({ active: !client.active, version: client.version });
          setActiveConfirmOpen(false);
        }}
      />
    </>
  );
}
